import { strapiAuthProvider, strapiDataProvider, strapiHttpClient } from "ra-strapi";
import { STRAPI_BASE_URL } from '../../../config/services/strapi/strapi';

const authProvider = strapiAuthProvider({
  baseURL: STRAPI_BASE_URL as string,
});
const httpClient = strapiHttpClient();
const dataProvider = strapiDataProvider({
  baseURL: STRAPI_BASE_URL as string,
  httpClient,
});

const strapiToRaRecord = (baseURL: string) => {
  const addBaseURLRecursively: any = (data: any) => {
    if (!data) return;
    if (Array.isArray(data)) {
      return data.map((item) => addBaseURLRecursively(item));
    }
    if (data.hasOwnProperty("url")) {
      const { url, ...rest } = data;
      const updatedRest = Object.entries(rest).reduce((acc: any, [key, value]) => {
        acc[key] =
          typeof value === "object" ? addBaseURLRecursively(value) : value;
        return acc;
      }, {});
      return {
        ...updatedRest,
        url: url.includes("http") ? url : `${baseURL}${url}`,
      };
    }
    return Object.entries(data).reduce((acc: any, [key, value]) => {
      acc[key] =
        typeof value === "object" ? addBaseURLRecursively(value) : value;
      return acc;
    }, {});
  };
  const toRaAttributes = (attributes: any) => {
    Object.keys(attributes).forEach((key: string) => {
      const data = attributes[key];
      if (!data) return;

      if (data.hasOwnProperty("mime")) {
        attributes[key] = addBaseURLRecursively(data);
      }

      else if (data.hasOwnProperty("documentId")) {
        attributes[key] = addBaseURLRecursively(data);
      }

      else if (Array.isArray(data) && data.length > 0 && data[0]?.documentId) {
        attributes[key] = data.map((item: any) => addBaseURLRecursively(item));
      }
    });

    return attributes;
  };

  return (data: any) => {
    const { documentId, id, blocks, ...attributes } = data;
    return {
      id: documentId,
      ref: id,
      ...toRaAttributes(attributes),
    };
  };
};

const getToken = () => {
  try {
    const token = localStorage.getItem('strapi_jwt');
    if (!token) return null;

    return token;
  } catch {
    return null;
  }
};

const readBodySafe = async (response: Response) => {
  const contentType = response.headers.get('content-type') || '';
  const text = await response.text();

  if (!text) return { json: null as any, text: '' };

  const looksJson = contentType.includes('application/json') || text.trim().startsWith('{') || text.trim().startsWith('[');
  if (!looksJson) return { json: null as any, text };

  try {
    return { json: JSON.parse(text), text };
  } catch {
    return { json: null as any, text };
  }
};

const getErrorMessage = (fallback: string, body: any, text: string) => {
  if (typeof body?.error?.message === 'string') return body.error.message;
  if (typeof body?.message === 'string') return body.message;
  if (typeof text === 'string' && text.trim()) return text;
  return fallback;
};

const requestJson = async (url: string, init: RequestInit) => {
  const response = await fetch(url, init);
  const { json, text } = await readBodySafe(response);

  if (!response.ok) {
    const message = getErrorMessage(response.statusText || 'Request failed', json, text);
    const err: any = new Error(message);
    err.status = response.status;
    err.body = json ?? text;
    throw err;
  }

  return json;
};

export const customAuthProvider = {
  ...authProvider,
  login: async (params: any) => {
    await authProvider.login(params);
    return {
      redirectTo: '/admin'
    };
  }
}

export const customDataProvider = {
  ...dataProvider,

  async getOne(resource: string, params: any) {
    const toRaRecord = strapiToRaRecord(STRAPI_BASE_URL as string);
    const url = `${STRAPI_BASE_URL}/api/${resource}/${params.id}?pLevel`;
    const { data } = await httpClient(url).then((res: any) => res.json);

    const record = {
      id: params.id,
      ...data
    }

    if (resource === 'empresasgasco-ejecutivos') {
      record.empresa = record.empresa.documentId;
    }

    if (resource === 'empresasgasco-memoria-corporativas' && record.informacion_corporativa) {
      record.informacion_corporativa = record.informacion_corporativa.documentId;
    }

    if (resource === 'empresasgasco-junta-accionistass' && record.informacion_accionista) {
      record.informacion_accionista = record.informacion_accionista.documentId;
    }

    return {
      data: toRaRecord(record)
    };
  },

  async getList(resource: string, params: any) {
    const toRaRecord = strapiToRaRecord(STRAPI_BASE_URL as string);
    const url = `${STRAPI_BASE_URL}/api/${resource}?pLevel`;
    const { data } = await httpClient(url).then((res: any) => res.json);

    const records = data.map((item: any) => {
      return {
        id: item.id,
        ...item,
      };
    });

    return {
      data: records.map((record: any) => toRaRecord(record)),
      total: data.length
    };
  },

  async create(resource: string, params: any) {
    const toRaRecord = strapiToRaRecord(STRAPI_BASE_URL as string);
    const body = JSON.stringify({
      data: params.data,
    });
    const token = getToken();

    const baseHeaders = {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
    };

    try {
      const json = await requestJson(`${STRAPI_BASE_URL}/api/${resource}`, {
        method: 'POST',
        body,
        headers: baseHeaders,
      });

      const record = json?.data ?? json;
      return { data: toRaRecord(record) };
    } catch (error: any) {
      if (error?.status === 405) {
        // Algunos endpoints están configurados sin POST (p.ej. single-type o reglas del gateway).
        // Intentamos un fallback con PUT al endpoint base.
        const json = await requestJson(`${STRAPI_BASE_URL}/api/${resource}`, {
          method: 'PUT',
          body,
          headers: baseHeaders,
        });

        const record = json?.data ?? json;
        return { data: toRaRecord(record) };
      }

      throw error;
    }
  },

  async update(resource: string, params: any) {
    const toRaRecord = strapiToRaRecord(STRAPI_BASE_URL as string);
    const body = JSON.stringify({
      data: params.data,
    });
    const token = getToken();

    const baseHeaders = {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
    };

    try {
      const json = await requestJson(`${STRAPI_BASE_URL}/api/${resource}/${params.id}`, {
        method: 'PUT',
        body,
        headers: baseHeaders,
      });

      const record = json?.data ?? json;
      return { data: toRaRecord(record) };
    } catch (error: any) {
      if (error?.status === 405) {
        const json = await requestJson(`${STRAPI_BASE_URL}/api/${resource}`, {
          method: 'PUT',
          body,
          headers: baseHeaders,
        });

        const record = json?.data ?? json;
        return { data: toRaRecord(record) };
      }

      throw error;
    }
  }
}